// @ts-check
import { parse, differenceInMinutes, startOfDay, addMinutes, addDays } from 'date-fns';
import { buildVisitScheduleItem } from './buildVisitScheduleItem';
import { buildPadPrescriptionEvent } from './buildPadPrescriptionEvent';
import { SHIFT_START_MINUTES } from '../constants/shiftConstants';

/** @typedef {number|null} TMaxTimeDifferenceMinutes   */
/** @typedef {string|null} TShiftStartTime   */

/**
 * @typedef {import('app/redux/features/pad-initialization/reducer/visit-schedule-step/utils/transformPadPrescriptionsToPrescriptionLoadSchedule').PadVisit} TPadVisit
 */

/**
 * Agrupa eventos com base em uma janela de tempo máxima e limites de turno.
 * Eventos só são agrupados se estiverem dentro da janela de tempo E no mesmo turno.
 *
 * Em visitas de plantão, os eventos recebidos já devem estar filtrados pela
 * janela contratada. Regra completa em `applyShiftCoverageRules.js` (#2205).
 *
 * @param {Object} params - Lista de eventos (DtEvent) a serem agrupados.
 * @param {import('./buildPadPrescriptionEvent').DtEvent[]} params.dateTimeEvents - Lista de eventos (DtEvent) a serem agrupados.
 * @param {TShiftStartTime} params.shiftStartTime  - Hora de início do plantão.
 * @param {TMaxTimeDifferenceMinutes} [params.maxTimeDifferenceMinutes=60] - Diferença máxima em minutos para agrupamento.
 * @returns {Map<string, any>} Um Map contendo as visitas agrupadas.
 */
export function applyGroupEventsByTimeGapRules({
  dateTimeEvents,
  shiftStartTime,
  maxTimeDifferenceMinutes
}) {
  // Validação rápida da entrada
  if (!Array.isArray(dateTimeEvents) || dateTimeEvents.length === 0) {
    return new Map();
  }

  if (!maxTimeDifferenceMinutes) {
    maxTimeDifferenceMinutes = 60;
  }

  // 1. Ordena os eventos cronologicamente para que a busca por proximidade funcione
  const dtEventSortedList = [...dateTimeEvents].sort(
    (a, b) => toDateTimeISO(a).getTime() - toDateTimeISO(b).getTime()
  );

  const scheduleMap = new Map();

  // Array auxiliar para sabermos quais chaves/visitas já criamos e comparar os tempos
  const createdVisitsTimeline = [];

  // 2. Percorre os eventos ordenados
  // eslint-disable-next-line no-unused-vars
  for (const currentDtEvent of dtEventSortedList) {
    const { date, time, padPrescriptionId /* , padPrescription */ } = currentDtEvent;
    const currentEventDate = toDateTimeISO({ date, time });

    let matchedVisitKey = null;

    const shiftStartInMinutes = getShiftStartInMinutes(shiftStartTime);

    // Verifica se este evento se encaixa em alguma visita já criada na timeline
    // Como está ordenado, geralmente fará sentido comparar com os últimos criados
    for (let i = createdVisitsTimeline.length - 1; i >= 0; i--) {
      const existingVisit = createdVisitsTimeline[i];
      const diff = Math.abs(differenceInMinutes(currentEventDate, existingVisit.dateTime));

      // Usa getShiftBoundaries para verificar se ambos eventos estão no mesmo turno
      const currentBoundaries = getShiftBoundaries({
        dateTime: currentEventDate,
        maxTimeDifferenceMinutes,
        shiftStartInMinutes
      });
      const existingBoundaries = getShiftBoundaries({
        dateTime: existingVisit.dateTime,
        maxTimeDifferenceMinutes,
        shiftStartInMinutes
      });

      const sameShift =
        currentBoundaries.shiftStart.getTime() === existingBoundaries.shiftStart.getTime() &&
        currentBoundaries.shiftEnd.getTime() === existingBoundaries.shiftEnd.getTime();

      if (diff <= maxTimeDifferenceMinutes && sameShift) {
        matchedVisitKey = existingVisit.uniqueVisitKey;
        break; // Encontrou a visita correspondente dentro da janela de tempo e mesmo turno
      }
    }

    if (matchedVisitKey) {
      // Se encontrou uma visita próxima, apenas adiciona o ID do medicamento a ela
      const visit = scheduleMap.get(matchedVisitKey);
      visit.padPrescriptionIds.push(padPrescriptionId);

      const nEvent = buildPadPrescriptionEvent(currentDtEvent);
      visit.padPrescriptionEvents.push(nEvent);
    } else {
      // Se não encontrou nenhuma visita no limite de tempo, cria uma NOVA visita
      // const uniqueVisitKey = `${date.replace(/\//g, '-')}-${time.replace(/:/g, '')}`;
      const uniqueVisitKey = toDateTimeISO({ date, time }).toISOString();
      const newItem = buildVisitScheduleItem(currentDtEvent);

      scheduleMap.set(uniqueVisitKey, {
        ...newItem,
        uniqueVisitKey
      });

      // Salva na timeline para comparar com os próximos eventos do loop
      createdVisitsTimeline.push({
        uniqueVisitKey,
        dateTime: currentEventDate
      });
    }
  }

  return scheduleMap;
}

/**
 * Retorna a data/hora de início do ciclo de turnos para a data do evento.
 * Este é o início do "ciclo de turnos" para aquela data.
 *
 * @param {Date} dateTime - Data/hora do evento
 * @param {number} minutesFromStartOfDay - Minutos desde o início do dia para o início do turno (ex: 420 para 07:00)
 * @returns {Date} Início do ciclo de turnos do dia do evento (ajustado se antes do horário de início)
 */
function getShiftCycleStart(dateTime, minutesFromStartOfDay) {
  const dayStart = startOfDay(dateTime);
  const shiftStart = addMinutes(dayStart, minutesFromStartOfDay);

  // Se o evento é antes do horário de início do turno, pertence ao ciclo que começou no dia anterior
  if (new Date(dateTime).getTime() < shiftStart.getTime()) {
    return addDays(shiftStart, -1);
  }
  return shiftStart;
}

/**
 * Retorna os limites (início e fim) do turno para um dateTime específico.
 * Turnos são blocos de maxTimeDifferenceMinutes iniciando no shiftStartInMinutes configurado.
 *
 * @param {Object} params
 * @param {Date} params.dateTime - Data/hora de referência
 * @param {number} params.maxTimeDifferenceMinutes - Duração do turno em minutos (ex: 720 para 12h)
 * @param {number|null} params.shiftStartInMinutes - Minutos desde o início do dia para o início do turno
 * @returns {{ shiftStart: Date, shiftEnd: Date }} Objeto com início e fim do turno
 */
export function getShiftBoundaries({ dateTime, maxTimeDifferenceMinutes, shiftStartInMinutes }) {
  const minutesFromStartOfDay = shiftStartInMinutes || SHIFT_START_MINUTES;
  const cycleStart = getShiftCycleStart(dateTime, minutesFromStartOfDay);
  const minutesSinceCycleStart = differenceInMinutes(dateTime, cycleStart);
  const shiftIndex = Math.floor(minutesSinceCycleStart / maxTimeDifferenceMinutes);

  const actualShiftStart = addMinutes(cycleStart, shiftIndex * maxTimeDifferenceMinutes);
  const actualShiftEnd = addMinutes(actualShiftStart, maxTimeDifferenceMinutes);
  return { shiftStart: actualShiftStart, shiftEnd: actualShiftEnd };
}

/**
 * Converte um objeto com data e hora string para um objeto Date.
 *
 * @param {Object} param0 - O objeto contendo a data e a hora.
 * @param {string} param0.date - A data no formato "DD/MM/YYYY" (ex: "22/06/2026").
 * @param {string} param0.time - A hora no formato "HH:mm" ou "HH:mm:ss" (ex: "14:30").
 * @throws {Error} Se a string gerada não for uma data/hora válida.
 * @returns {Date} Um objeto Date válido.
 */
export const toDateTimeISO = ({ date, time }) => {
  const [day, month, year] = date.trim().split('/');
  const [hour, minute] = time.trim().split(':');

  // O construtor de Date recebe o mês a partir de 0 (janeiro = 0). Sem o "- 1",
  // datas como 31/10 e 01/11 viram o mesmo instante e as visitas colidem (#2205).
  const dateTime = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute)
  );

  const isoString = dateTime.toISOString();

  const parsed = parse(isoString);

  if (isNaN(parsed.getTime())) {
    throw new Error(`Data/hora inválida: ${date} ${time}`);
  }

  return parsed;
};

/**
 * Converte uma hora no formato "hh:mm" em minutos desde o início do dia.
 * @param {string|null} hourMinute - Hora no formato "hh:mm" (ex: "07:30")
 * @returns {number} Minutos desde o início do dia
 */
export function getShiftStartInMinutes(hourMinute) {
  if (!hourMinute || typeof hourMinute !== 'string') {
    return SHIFT_START_MINUTES;
  }

  const parts = hourMinute.split(':');
  if (parts.length !== 2) {
    return SHIFT_START_MINUTES;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return SHIFT_START_MINUTES;
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return SHIFT_START_MINUTES;
  }

  return hours * 60 + minutes;
}
